import { differenceInSeconds, secondsInMinute } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getIdleTime } from '../../services/admin/settings/systemSettings.service';
import { Paths } from '../../utils/routerUrls';
import { IdlePrompt } from '../Prompt/Idle/IdlePrompt.component';
import { leaveAssessmentSession } from '../../Context/SignalR.context';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setAssessmentSessionEvent } from '../../services/scoring/scoring.service';
import useSessionUser, { isUserLoggedIn, retrieveUserFromStorage } from '../../hooks/useSessionUser';
import { needToNotifyAboutLeaveAssessmentProcess } from '../../utils/assessmentSessionProcess';
import { useIdleMonitorSync } from './hooks/useIdleMonitorSync';

interface IdleMonitorProps {
  /** Default time duration at which logout will happen represented in seconds if no value is retrieved from backend */
  defaultTimeout: number;
}

export const IdleMonitor: React.FC<IdleMonitorProps> = (props) => {
  const history = useHistory();
  const [idleTime, setIdleTime] = useState<number>(0);
  const [lastActivityTime, setLastActivityTime] = useState<Date>(new Date());
  const [isIdle, setIsIdle] = useState<boolean>(false);
  const [timeout, setTimeout] = useState<number | string>(props.defaultTimeout);

  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const { userAssessmentSessionInfo } = useSessionUser();
  const userFunction = userAssessmentSessionInfo.function;

  const examinerIsWorking = sessionStorage.getItem('startWork');

  const user = useSessionUser();
  const isExaminer = user.user?.profile.account_type === 'examiner';

  const idleOn = (timeout as number) - 60; //warn 1 minute before reaching timeout;

  const onStillWorkingFromAnotherTab = useCallback(() => {
    setIdleTime(0);
    setLastActivityTime(new Date());
    setIsIdle(false);
  }, []);

  const { emitActivityMessage, tabId } = useIdleMonitorSync({
    isIdle,
    setIdleTime,
    onStillWorkingFromAnotherTab,
  });

  const isLoggedIn = !!user.user;

  useEffect(() => {
    if (isLoggedIn) {
      getIdleTime()
        .then((result) => {
          setTimeout(result ? (result as number) * secondsInMinute : props.defaultTimeout);
        })
        .catch(() => setTimeout(props.defaultTimeout));
    }
  }, [isLoggedIn]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const onActivity = () => {
      setIdleTime(0);
      setLastActivityTime(new Date());
      setIsIdle(false);
      emitActivityMessage({ tabId: tabId.current });
    };

    const startTimer = () => {
      intervalId = setInterval(() => {
        setIdleTime((prevIdleTime) => prevIdleTime + 5);
      }, 5000);
    };

    const stopTimer = () => {
      clearInterval(intervalId);
    };

    startTimer();
    window.addEventListener('mousedown', onActivity);
    window.addEventListener('keydown', onActivity);

    return () => {
      stopTimer();
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, [idleTime, emitActivityMessage]);

  useEffect(() => {
    if (idleTime >= idleOn || differenceInSeconds(new Date(), lastActivityTime) >= idleOn) {
      setIsIdle(true);
    }
    if (idleTime >= +timeout || differenceInSeconds(new Date(), lastActivityTime) >= +timeout) {
      if (examinerIsWorking === 'true' && isExaminer) {
        leaveAssessmentSession(assessmentSession);
        if (needToNotifyAboutLeaveAssessmentProcess(assessmentSession, userFunction)) {
          const isLoggedIn = isUserLoggedIn();
          if (isLoggedIn)
            setAssessmentSessionEvent(2).then(
              () => {
                sessionStorage.removeItem('startWork');
                history.push(Paths.logout);
              },
              (error) => {
                console.log(error);
              },
            );
        } else {
          sessionStorage.removeItem('startWork');
          history.push(Paths.logout);
        }
      } else {
        history.push(Paths.logout);
      }
    }
  }, [idleTime, timeout, lastActivityTime, idleOn]);

  const stillWorking = () => {
    setIdleTime(0);
  };

  return isIdle ? <IdlePrompt onConfirm={stillWorking} /> : null;
};
